import React, { ReactNode, useId, useMemo } from "react"
import { Typography, typographyTypes } from "../Typography"
import CheckboxV2 from "../CheckboxV2"
import { ButtonWrapper } from "../ButtonWrapper"
import {
  CheckBoxState,
  PaginationProps,
  SortDirection,
  TableHeader as TableHeaderType,
  TableRow as TableRowType,
} from "./types"
import useIsMobile from "../../hooks/useIsMobile"
import TableCards from "./TableCards"
import { TableHeader } from "./TableHeader"
import { TableCell } from "./TableCell"
import { TableGrid } from "./TableGrid"
import { TablePagination } from "./TablePagination"
import { DATA_FOCUSABLE_CELL } from "./tableUtils"
import { ROWS_PER_PAGE_OPTIONS, useTablePagination } from "./useTablePagination"
import { useTableKeyboardNavigation } from "./useTableKeyboardNavigation"
import { useValidateTableData } from "./useValidateTableData"
import { useTableAriaLive } from "./useTableAriaLive"
import { AriaLiveRegion } from "../../../ariaLive/components/AriaLiveRegion"
import { SemanticHeader } from "../SemanticHeader"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import { populateCmsTemplate } from "../../utils"
import cn from "../../../../utils/classes"
import SkeletonLoader from "../SkeletonLoader"
import Icon from "../Icon"

interface DynamicTableProps {
  headers: TableHeaderType[]
  rows: TableRowType[]
  showDeleteColumn?: boolean
  showEditColumn?: boolean
  onCheckboxStateChange?: (checkboxState: CheckBoxState) => void
  headerGroups?: string[]
  paginationProps?: PaginationProps
  loading?: boolean
  showTotalResults?: boolean
  noResultsMessage?: string
  tableTitle?: string
  tableAriaLabel?: string
  isHardDelete?: boolean
  pageResetKey?: string | number
  styleOverrides?: string
  tableStyleOverrides?: string
}

const TableHeaderTypography = ({ text }: { text: string }) => {
  return (
    <Typography
      text={text}
      variant={typographyTypes.NORMAL}
      styleOverrides="font-semibold !text-[12px] uppercase"
    />
  )
}

const TableCellTypography = ({ text }: { text: string }) => {
  return (
    <Typography
      text={text}
      variant={typographyTypes.TABLE}
      styleOverrides="whitespace-pre-line break-words"
    />
  )
}

const DividerElement = () => (
  <hr
    className={
      "m-0 min-h-[31px] bg-gray-50 border-0 group-focus-visible:ring-2 group-focus-visible:ring-inset group-focus-visible:ring-[#2198CC] group-focus-visible:rounded-lg"
    }
  />
)

const PlaceholderCell = ({
  rowIndex,
  text = "--",
  ...props
}: {
  rowIndex: number
  text?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}) => (
  <TableCell
    id={`edit-row-${rowIndex}-placeholder`}
    className="text-center"
    {...props}
  >
    <TableCellTypography text={text} />
  </TableCell>
)

export const DynamicTable: React.FC<DynamicTableProps> = ({
  headers,
  rows,
  showDeleteColumn,
  showEditColumn,
  onCheckboxStateChange,
  headerGroups,
  paginationProps,
  loading,
  showTotalResults = false,
  noResultsMessage,
  tableTitle = "",
  tableAriaLabel,
  isHardDelete = false,
  pageResetKey,
  styleOverrides = "",
  tableStyleOverrides = "",
}) => {
  const {
    cms: { common: commonText },
  } = useCMSReady(["common"])

  // CMS text values extracted for useMemo deps
  const noResultsMessageEff =
    noResultsMessage ??
    commonText?.tableNoResultsText ??
    "No results found at this time."
  const placeholderText = commonText?.tablePlaceholderText ?? "--"
  const selectAriaLabel = commonText?.tableSelectAriaLabel ?? "Select"
  const selectAllRowsLabel =
    commonText?.tableSelectAllRowsLabel ?? "Select all rows"
  const sortByTemplate = commonText?.tableSortByAriaLabelTemplate
  const checkRowTemplate = commonText?.tableCheckRowLabelTemplate
  const editHeaderText = commonText?.tableEditHeader ?? "EDIT"
  const deleteHeaderText = isHardDelete
    ? (commonText?.tableRemoveHeader ?? "REMOVE")
    : (commonText?.tableArchiveHeader ?? "ARCHIVE")
  const tableAriaLabelEff =
    tableAriaLabel ?? commonText?.tableAriaLabel ?? "Data table"

  // Auto-infer edit/delete column visibility from row data when not explicitly set
  const showEditColumnEff =
    showEditColumn ?? rows.some((row) => typeof row.onEditClick === "function")
  const showDeleteColumnEff =
    showDeleteColumn ??
    rows.some((row) => typeof row.onDeleteClick === "function")

  const isMobile = useIsMobile()
  const tableId = useId()
  const paginationAriaId = `${tableId}-pagination`
  const sortAriaId = `${tableId}-sort`

  const {
    numberOfPages,
    currentPage,
    rowsPerPage,
    loadingEff,
    paginatedData,
    sortState,
    setSortState,
    resetCheckboxState,
    handlePageChange,
    handleRowsPerPageChange,
    showingRowsMessage,
    totalResultsMessage,
  } = useTablePagination({
    rows,
    headers,
    paginationProps,
    loading,
    onCheckboxStateChange,
    pageResetKey,
  })

  const showGroupHeaders = useMemo(
    () => !!(headerGroups && headerGroups.length > 0),
    [headerGroups],
  )

  const headerRowCount = showGroupHeaders ? 2 : 1
  const totalEntries = paginationProps?.totalCount ?? rows.length
  const tableAriaRowCount =
    numberOfPages > 1 ? headerRowCount + totalEntries : undefined
  const tableFirstDataRowIndex =
    numberOfPages > 1
      ? headerRowCount + (currentPage - 1) * rowsPerPage + 1
      : undefined

  const showCheckboxColumn = useMemo(
    () => typeof onCheckboxStateChange === "function" && !isMobile,
    [onCheckboxStateChange, isMobile],
  )

  useTableAriaLive({
    loadingEff,
    totalResultsMessage,
    sortState,
    headers,
    paginationAriaId,
    sortAriaId,
  })

  const tableCardHeaderGroupsEff = useMemo(() => {
    if (!headerGroups || headerGroups.length !== headers.length) {
      return undefined
    }
    const displayGroups: string[] = [...headerGroups]
    if (showEditColumnEff) displayGroups.push("")
    if (showDeleteColumnEff) displayGroups.push("")
    return displayGroups
  }, [headerGroups, headers.length, showEditColumnEff, showDeleteColumnEff])

  const {
    focusedCell,
    setFocusedCell,
    isCellFocused,
    isAnyCellFocused,
    handleTableKeyDown,
    handleMobileTableKeyDown,
  } = useTableKeyboardNavigation({
    paginatedData,
    headers,
    showGroupHeaders,
    showCheckboxColumn,
    showEditColumn: showEditColumnEff,
    showDeleteColumn: showDeleteColumnEff,
    isMobile,
    tableCardHeaderGroupsEff,
  })

  useValidateTableData({ headers, rows, headerGroups })

  const tableGridHeaderGroupsEff = useMemo(() => {
    if (!headerGroups || headerGroups.length !== headers.length) return []

    let displayGroups: string[] = [...headerGroups]
    if (typeof onCheckboxStateChange === "function") {
      displayGroups = ["", ...displayGroups]
    }
    if (showEditColumnEff) displayGroups.push("")
    if (showDeleteColumnEff) displayGroups.push("")

    const topHeaderGroups: { label: string; colSpan: number }[] = []
    let currentLabel: string = displayGroups[0]
    let currentCount = 1
    for (let i = 1; i <= displayGroups.length; i++) {
      if (i < displayGroups.length && displayGroups[i] === currentLabel) {
        currentCount++
      } else {
        topHeaderGroups.push({ label: currentLabel, colSpan: currentCount })
        currentLabel = displayGroups[i]
        currentCount = 1
      }
    }

    const topGroupHeadersEff: ReactNode[] = []
    let groupOffset = 0
    topHeaderGroups.map((group, index) => {
      let dataValue = `0-${index + groupOffset}`
      for (let i = 0; i < group.colSpan - 1; i++) {
        groupOffset++
        dataValue += `,0-${index + groupOffset}`
      }
      topGroupHeadersEff.push(
        <TableHeader
          key={`top-header-${index}`}
          colSpan={group.colSpan}
          {...{ [DATA_FOCUSABLE_CELL]: dataValue }}
          tabIndex={isCellFocused(0, index + groupOffset) ? 0 : -1}
          onKeyDown={handleTableKeyDown}
          className={cn("!text-center", {
            "border-0 !border-l-[1px] border-solid border-gray-200":
              index !== 0,
          })}
        >
          <TableHeaderTypography text={group.label} />
        </TableHeader>,
      )
    })
    return topGroupHeadersEff
  }, [
    handleTableKeyDown,
    headerGroups,
    headers.length,
    isCellFocused,
    onCheckboxStateChange,
    showDeleteColumnEff,
    showEditColumnEff,
  ])

  const tableGridHeadersEff = useMemo(() => {
    const headersEff = []
    const includeCheckboxColumn =
      typeof onCheckboxStateChange === "function" && !isMobile
    const headersRow = showGroupHeaders ? 1 : 0

    if (includeCheckboxColumn) {
      headersEff.push(
        <TableHeader
          key="checkbox-header"
          scope="col"
          aria-label={selectAriaLabel}
          className="text-center"
        >
          <div className="flex flex-row items-center justify-center">
            <CheckboxV2
              onChange={(e) =>
                onCheckboxStateChange(
                  paginatedData.reduce(
                    (acc, row) => ({ ...acc, [row.data.id]: e.target.checked }),
                    {},
                  ),
                )
              }
              checked={paginatedData.every((row) => row.checkboxChecked)}
              showLabel={false}
              label={selectAllRowsLabel}
              containerStyleOverrides="flex justify-center"
              tabIndex={isCellFocused(headersRow, 0) ? 0 : -1}
              {...{ [DATA_FOCUSABLE_CELL]: `${headersRow}-0` }}
              onKeyDown={handleTableKeyDown}
              forceFocus={isCellFocused(headersRow, 0)}
            />
          </div>
        </TableHeader>,
      )
    }

    headers.map((header, index) => {
      const isSorted = sortState.col === index
      let sortIcon = null
      if (isSorted && sortState.dir === SortDirection.ASC) {
        sortIcon = <span style={{ marginLeft: 4 }}>▲</span>
      } else if (isSorted && sortState.dir === SortDirection.DESC) {
        sortIcon = <span style={{ marginLeft: 4 }}>▼</span>
      }

      const allowHeaderSorting = !header.noSort && !isMobile
      const col = includeCheckboxColumn ? index + 1 : index

      const handleHeaderClickAndEnter = () => {
        setSortState((prev) => {
          if (prev.col !== index) {
            return { col: index, dir: SortDirection.ASC }
          } else if (prev.dir === SortDirection.ASC) {
            return { col: index, dir: SortDirection.DESC }
          } else if (prev.dir === SortDirection.DESC) {
            return { col: null, dir: null }
          } else {
            return { col: index, dir: SortDirection.ASC }
          }
        })
        resetCheckboxState()
      }

      const headerEff = allowHeaderSorting ? (
        // make this whole header a touch target for the span button below that actually triggers the sorting, so that it's easier to tap on mobile
        <TableHeader
          key={index}
          scope="col"
          aria-sort={`${isSorted ? (sortState.dir === SortDirection.ASC ? "ascending" : "descending") : "none"}`}
          className="cursor-pointer hover:bg-gray-300 justify-center"
          onClick={handleHeaderClickAndEnter}
        >
          <button
            type="button"
            aria-label={
              populateCmsTemplate(
                [header.label],
                "cms.common.tableSortByAriaLabelTemplate",
                sortByTemplate,
              ) || `Sort by ${header.label}`
            }
            onKeyDown={handleTableKeyDown}
            tabIndex={isCellFocused(headersRow, col) ? 0 : -1}
            {...{ [DATA_FOCUSABLE_CELL]: `${headersRow}-${col}` }}
            className="text-left whitespace-nowrap bg-transparent border-0 cursor-pointer p-0 flex flex-row items-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2198CC] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
          >
            {header.headerNode ?? <TableHeaderTypography text={header.label} />}
            {sortIcon}
          </button>
        </TableHeader>
      ) : (
        <TableHeader
          key={index}
          scope="col"
          className="justify-center"
          tabIndex={isCellFocused(headersRow, col) ? 0 : -1}
          {...{ [DATA_FOCUSABLE_CELL]: `${headersRow}-${col}` }}
          onKeyDown={handleTableKeyDown}
        >
          {header.headerNode ?? <TableHeaderTypography text={header.label} />}
        </TableHeader>
      )

      headersEff.push(headerEff)
    })

    const editCol = includeCheckboxColumn ? headers.length + 1 : headers.length
    if (showEditColumnEff) {
      headersEff.push(
        <TableHeader
          key="edit-header"
          scope="col"
          className="text-center"
          tabIndex={isCellFocused(headersRow, editCol) ? 0 : -1}
          {...{ [DATA_FOCUSABLE_CELL]: `${headersRow}-${editCol}` }}
          onKeyDown={handleTableKeyDown}
        >
          <TableHeaderTypography text={editHeaderText} />
        </TableHeader>,
      )
    }
    const deleteCol = includeCheckboxColumn
      ? headers.length + (showEditColumnEff ? 2 : 1)
      : headers.length + (showEditColumnEff ? 1 : 0)
    if (showDeleteColumnEff) {
      headersEff.push(
        <TableHeader
          key="delete-header"
          scope="col"
          className="text-center"
          tabIndex={isCellFocused(headersRow, deleteCol) ? 0 : -1}
          {...{ [DATA_FOCUSABLE_CELL]: `${headersRow}-${deleteCol}` }}
          onKeyDown={handleTableKeyDown}
        >
          <TableHeaderTypography text={deleteHeaderText} />
        </TableHeader>,
      )
    }
    return headersEff
  }, [
    onCheckboxStateChange,
    isMobile,
    showGroupHeaders,
    headers,
    showEditColumnEff,
    showDeleteColumnEff,
    paginatedData,
    isCellFocused,
    handleTableKeyDown,
    sortState.col,
    sortState.dir,
    setSortState,
    resetCheckboxState,
    selectAriaLabel,
    selectAllRowsLabel,
    sortByTemplate,
    editHeaderText,
    deleteHeaderText,
  ])

  // TODO: handle no rows case (e.g. show "No results" message when rows is empty)
  const hasNoData =
    !loadingEff && rows.length === 0 && !paginationProps?.totalCount

  const tableGridRowsEff = useMemo(() => {
    const rows: JSX.Element[][] = []

    paginatedData.map((row, rowIndex) => {
      const cells = []

      const showCheckboxColumnEff =
        showCheckboxColumn && typeof onCheckboxStateChange === "function"
      const rowShowEdit =
        showEditColumnEff && typeof row.onEditClick === "function"
      const rowShowDelete =
        showDeleteColumnEff && typeof row.onDeleteClick === "function"

      const tableDataRow = rowIndex + 1 + (showGroupHeaders ? 1 : 0)

      if (showCheckboxColumnEff) {
        cells.push(
          <TableCell
            id={`checkbox-row-${rowIndex}`}
            key={`checkbox-row-${rowIndex}`}
          >
            <CheckboxV2
              onChange={(event) => row.onCheckboxChange!(event, row.data)}
              checked={row.checkboxChecked}
              showLabel={false}
              label={
                populateCmsTemplate(
                  [String(tableDataRow)],
                  "cms.common.tableCheckRowLabelTemplate",
                  checkRowTemplate,
                ) || `Check row ${tableDataRow}`
              }
              containerStyleOverrides="flex justify-center"
              tabIndex={isCellFocused(tableDataRow, 0) ? 0 : -1}
              {...{ [DATA_FOCUSABLE_CELL]: `${tableDataRow}-0` }}
              onKeyDown={handleTableKeyDown}
              forceFocus={isCellFocused(tableDataRow, 0)}
            />
          </TableCell>,
        )
      }
      row.data.cells.map((cell, colIndex) => {
        const tableDataCol = colIndex + (showCheckboxColumnEff ? 1 : 0)
        const cellValue = cell.value
        let nodeClone: React.ReactNode = null
        if (cell.node) {
          nodeClone = React.isValidElement(cell.node)
            ? React.cloneElement(cell.node, {
                key: cell.id,
                tabIndex: isCellFocused(tableDataRow, tableDataCol) ? 0 : -1,
                ...{ [DATA_FOCUSABLE_CELL]: `${tableDataRow}-${tableDataCol}` },
                onKeyDown: handleTableKeyDown,
              })
            : cell.node
        }
        const tableCellProps =
          nodeClone === null || typeof cell.node === "string"
            ? {
                [DATA_FOCUSABLE_CELL]: `${tableDataRow}-${tableDataCol}`,
                tabIndex: isCellFocused(tableDataRow, tableDataCol) ? 0 : -1,
                onKeyDown: handleTableKeyDown,
              }
            : {}
        cells.push(
          <TableCell
            id={cell.id}
            key={`table-cell-${cell.id}`}
            className="text-left text-sm text-gray-700"
            {...tableCellProps}
          >
            {nodeClone ?? (
              <TableCellTypography text={String(cellValue ?? "")} />
            )}
          </TableCell>,
        )
      })
      if (showEditColumnEff) {
        const editCol = row.data.cells.length + (showCheckboxColumnEff ? 1 : 0)
        cells.push(
          rowShowEdit ? (
            <TableCell id={`edit-row-${rowIndex}`} key={`${row.data.id}-edit`}>
              <div className="w-full flex justify-center">
                <ButtonWrapper
                  onClick={() => row.onEditClick!(row.data)}
                  backgroundColor="transparent"
                  childStyleOverrides="!h-[20px]"
                  styleOverrides="p-0"
                  tabIndex={isCellFocused(tableDataRow, editCol) ? 0 : -1}
                  {...{ [DATA_FOCUSABLE_CELL]: `${tableDataRow}-${editCol}` }}
                  onKeyDown={handleTableKeyDown}
                  forceFocus={isCellFocused(tableDataRow, editCol)}
                >
                  <Icon iconType="editGray" />
                </ButtonWrapper>
              </div>
            </TableCell>
          ) : (
            <PlaceholderCell
              key={`${row.data.id}-edit`}
              rowIndex={rowIndex}
              text={placeholderText}
              tabIndex={isCellFocused(tableDataRow, editCol) ? 0 : -1}
              {...{ [DATA_FOCUSABLE_CELL]: `${tableDataRow}-${editCol}` }}
              onKeyDown={handleTableKeyDown}
            />
          ),
        )
      }
      if (showDeleteColumnEff) {
        const deleteCol =
          row.data.cells.length +
          (showCheckboxColumnEff ? 1 : 0) +
          (showEditColumnEff ? 1 : 0)
        cells.push(
          rowShowDelete ? (
            <TableCell
              id={`delete-row-${rowIndex}`}
              key={`${row.data.id}-delete`}
            >
              <div className="w-full flex justify-center">
                <ButtonWrapper
                  onClick={() => row.onDeleteClick!(row.data)}
                  backgroundColor="transparent"
                  childStyleOverrides="!h-[20px]"
                  styleOverrides="p-0"
                  tabIndex={isCellFocused(tableDataRow, deleteCol) ? 0 : -1}
                  {...{ [DATA_FOCUSABLE_CELL]: `${tableDataRow}-${deleteCol}` }}
                  onKeyDown={handleTableKeyDown}
                  forceFocus={isCellFocused(tableDataRow, deleteCol)}
                >
                  <Icon iconType="garbageCan" />
                </ButtonWrapper>
              </div>
            </TableCell>
          ) : (
            <PlaceholderCell
              key={`${row.data.id}-delete`}
              rowIndex={rowIndex}
              text={placeholderText}
              tabIndex={isCellFocused(tableDataRow, deleteCol) ? 0 : -1}
              {...{ [DATA_FOCUSABLE_CELL]: `${tableDataRow}-${deleteCol}` }}
              onKeyDown={handleTableKeyDown}
            />
          ),
        )
      }
      rows.push(cells)
    })

    return rows
  }, [
    paginatedData,
    showCheckboxColumn,
    onCheckboxStateChange,
    showEditColumnEff,
    showDeleteColumnEff,
    showGroupHeaders,
    isCellFocused,
    handleTableKeyDown,
    checkRowTemplate,
    placeholderText,
  ])

  const tableCardTablesEff = useMemo(() => {
    const tables = [] as ReactNode[][][]

    paginatedData.map((row, tableIdx) => {
      let prevGroupHeader = undefined as string | undefined
      let rowIndexEff = 0
      const rows = [] as ReactNode[][]

      const rowShowEdit =
        showEditColumnEff && typeof row.onEditClick === "function"
      const rowShowDelete =
        showDeleteColumnEff && typeof row.onDeleteClick === "function"

      const groupHeaderClassName = cn(
        "!p-0 h-[50px] text-center w-full border-solid border-0 !border-b-[2px] border-gray-200 bg-gray-50",
        { "h-[31px]": isMobile },
      )
      row.data.cells.map((cell, rowIdx) => {
        const newGroupHeader = !!tableCardHeaderGroupsEff
          ? tableCardHeaderGroupsEff[rowIdx]
          : undefined
        const isLeadingGroupHeader = prevGroupHeader === undefined
        const isNonLeadingEmptyGroupHeader =
          newGroupHeader !== "" || !isLeadingGroupHeader
        const showGroupHeader =
          !!tableCardHeaderGroupsEff &&
          isNonLeadingEmptyGroupHeader &&
          typeof newGroupHeader === "string" &&
          newGroupHeader !== prevGroupHeader

        if (showGroupHeader) {
          prevGroupHeader = newGroupHeader
          if (newGroupHeader === "") {
            rows.push([
              <TableCell
                id={`divider-${rowIndexEff}-${0}-${tableIdx}`}
                {...{
                  [DATA_FOCUSABLE_CELL]: `${rowIndexEff}-${0}-${tableIdx},${rowIndexEff}-${1}-${tableIdx}`,
                }}
                key={`divider-${rowIndexEff}-${0}-${tableIdx}`}
                colSpan={2}
                className={cn(groupHeaderClassName, "group")}
                tabIndex={isCellFocused(rowIndexEff, 0, tableIdx) ? 0 : -1}
                onKeyDown={handleMobileTableKeyDown}
              >
                <DividerElement />
              </TableCell>,
            ])
          } else {
            rows.push([
              <TableHeader
                id={`group-header-${rowIndexEff}-${0}-${tableIdx}`}
                {...{
                  [DATA_FOCUSABLE_CELL]: `${rowIndexEff}-${0}-${tableIdx},${rowIndexEff}-${1}-${tableIdx}`,
                }}
                key={`group-header-${rowIndexEff}-${0}-${tableIdx}`}
                className={groupHeaderClassName}
                colSpan={2}
                tabIndex={isCellFocused(rowIndexEff, 0, tableIdx) ? 0 : -1}
                onKeyDown={handleMobileTableKeyDown}
              >
                <TableHeaderTypography text={newGroupHeader} />
              </TableHeader>,
            ])
          }
          rowIndexEff += 1
        }

        const lengthEff =
          row.data.cells.length -
          1 +
          (rowShowEdit ? 1 : 0) +
          (rowShowDelete ? 1 : 0)
        const headerNode = (
          <TableHeader
            scope="row"
            className={cn("!border-0 py-1 px-4 w-1/2", {
              "!border-b-[1px]": rowIdx !== lengthEff,
            })}
            tabIndex={isCellFocused(rowIndexEff, 0, tableIdx) ? 0 : -1}
            {...{ [DATA_FOCUSABLE_CELL]: `${rowIndexEff}-0-${tableIdx}` }}
            onKeyDown={handleMobileTableKeyDown}
          >
            {headers[rowIdx].headerNode ?? (
              <TableHeaderTypography text={headers[rowIdx].label} />
            )}
          </TableHeader>
        )

        let nodeClone: React.ReactNode = null
        const cellValue = cell.value
        if (cell.node) {
          nodeClone = React.isValidElement(cell.node)
            ? React.cloneElement(cell.node as React.ReactElement, {
                id: `cell-${rowIndexEff}-${1}-${tableIdx}`,
                [DATA_FOCUSABLE_CELL]: `${rowIndexEff}-${1}-${tableIdx}`,
                tabIndex: isCellFocused(rowIndexEff, 1, tableIdx) ? 0 : -1,
                onKeyDown: handleMobileTableKeyDown,
              })
            : cell.node
        }
        const tableCellProps =
          nodeClone === null || typeof cell.node === "string"
            ? {
                [DATA_FOCUSABLE_CELL]: `${rowIndexEff}-${1}-${tableIdx}`,
                tabIndex: isCellFocused(rowIndexEff, 1, tableIdx) ? 0 : -1,
                onKeyDown: handleMobileTableKeyDown,
              }
            : {}
        const cellNode = (
          <TableCell
            id={cell.id}
            className={cn(
              "text-left text-sm text-gray-700 !border-0 w-1/2 !py-1 !px-4",
              {
                "!border-b-[1px]": rowIdx !== lengthEff,
              },
            )}
            {...tableCellProps}
          >
            {nodeClone ?? (
              <TableCellTypography text={String(cellValue ?? "")} />
            )}
          </TableCell>
        )
        rows.push([headerNode, cellNode])
        rowIndexEff += 1
      })

      const showEditDeleteSpacer =
        showGroupHeaders &&
        (showEditColumnEff || showDeleteColumnEff) &&
        prevGroupHeader !== ""
      if (showEditDeleteSpacer) {
        rows.push([
          <TableCell
            id={`divider-${rowIndexEff}-${0}-${tableIdx}`}
            {...{
              [DATA_FOCUSABLE_CELL]: `${rowIndexEff}-${0}-${tableIdx},${rowIndexEff}-${1}-${tableIdx}`,
            }}
            key={`divider-${rowIndexEff}-${0}-${tableIdx}`}
            colSpan={2}
            className={cn(groupHeaderClassName, "group")}
            tabIndex={isCellFocused(rowIndexEff, 0, tableIdx) ? 0 : -1}
            onKeyDown={handleMobileTableKeyDown}
          >
            <DividerElement />
          </TableCell>,
        ])
        rowIndexEff += 1
      }

      if (showEditColumnEff) {
        const editRow = rowIndexEff
        const editClassName = cn("!w-1/2 !py-1 !px-4", {
          "!border-b-0": !showDeleteColumnEff,
        })
        rows.push([
          <TableHeader
            key={`edit-header-${editRow}-${0}-${tableIdx}`}
            scope="row"
            className={editClassName}
            tabIndex={isCellFocused(editRow, 0, tableIdx) ? 0 : -1}
            {...{ [DATA_FOCUSABLE_CELL]: `${editRow}-0-${tableIdx}` }}
            onKeyDown={handleMobileTableKeyDown}
          >
            <TableHeaderTypography text={editHeaderText} />
          </TableHeader>,
          <TableCell
            id={`edit-row-${editRow}`}
            key={`edit-row-${editRow}`}
            className={editClassName}
          >
            {rowShowEdit ? (
              <ButtonWrapper
                key={`${row.data.id}-edit`}
                onClick={() => row.onEditClick!(row.data)}
                backgroundColor="transparent"
                childStyleOverrides="!h-[20px]"
                styleOverrides="p-0"
                tabIndex={isCellFocused(editRow, 1, tableIdx) ? 0 : -1}
                {...{ [DATA_FOCUSABLE_CELL]: `${editRow}-1-${tableIdx}` }}
                onKeyDown={handleMobileTableKeyDown}
                forceFocus={isCellFocused(editRow, 1, tableIdx)}
              >
                <Icon iconType="editGray" />
              </ButtonWrapper>
            ) : (
              <TableCellTypography text={placeholderText} />
            )}
          </TableCell>,
        ])
        rowIndexEff += 1
      }
      if (showDeleteColumnEff) {
        const deleteRow = rowIndexEff
        rows.push([
          <TableHeader
            key={`delete-header-${deleteRow}-${0}-${tableIdx}`}
            scope="row"
            className="w-1/2 py-1 px-4 !border-b-0"
            tabIndex={isCellFocused(deleteRow, 0, tableIdx) ? 0 : -1}
            {...{ [DATA_FOCUSABLE_CELL]: `${deleteRow}-0-${tableIdx}` }}
            onKeyDown={handleMobileTableKeyDown}
          >
            <TableHeaderTypography text={deleteHeaderText} />
          </TableHeader>,
          <TableCell
            id={`delete-row-${deleteRow}`}
            key={`delete-row-${deleteRow}-${0}-${tableIdx}`}
            className="w-1/2 !py-1 !px-4 !border-b-0"
          >
            {rowShowDelete ? (
              <ButtonWrapper
                key={`${row.data.id}-delete`}
                onClick={() => row.onDeleteClick!(row.data)}
                backgroundColor="transparent"
                childStyleOverrides="!h-[20px]"
                styleOverrides="p-0"
                tabIndex={isCellFocused(deleteRow, 1, tableIdx) ? 0 : -1}
                {...{ [DATA_FOCUSABLE_CELL]: `${deleteRow}-1-${tableIdx}` }}
                onKeyDown={handleMobileTableKeyDown}
                forceFocus={isCellFocused(deleteRow, 1, tableIdx)}
              >
                <Icon iconType="garbageCan" />
              </ButtonWrapper>
            ) : (
              <TableCellTypography text={placeholderText} />
            )}
          </TableCell>,
        ])
        rowIndexEff += 1
      }

      tables.push(rows)
    })

    return tables
  }, [
    paginatedData,
    showEditColumnEff,
    showDeleteColumnEff,
    isMobile,
    showGroupHeaders,
    tableCardHeaderGroupsEff,
    isCellFocused,
    handleMobileTableKeyDown,
    headers,
    editHeaderText,
    deleteHeaderText,
    placeholderText,
  ])

  if (hasNoData) return noResultsMessageEff

  return (
    <div className={cn("w-full min-w-0 space-y-2", styleOverrides)}>
      {/* Title */}
      {tableTitle && (
        <SemanticHeader
          headerLevel="h3"
          text={tableTitle}
          type="thirdLevelHeader"
          styleOverrides="m-0 mb-2"
        />
      )}
      {/* Total Results */}
      {showTotalResults &&
        (loadingEff ? (
          <SkeletonLoader width={100} height={20} />
        ) : (
          showTotalResults && (
            <Typography
              text={totalResultsMessage}
              variant={typographyTypes.HELPER}
            />
          )
        ))}
      {/* Mobile Table Component */}
      {isMobile && (
        <TableCards
          tables={tableCardTablesEff}
          loading={loadingEff}
          tableId={tableId}
          tabIndex={isAnyCellFocused ? -1 : 0}
          ariaLabel={tableAriaLabelEff}
          onFocus={() => {
            if (!focusedCell) {
              setFocusedCell({ row: 0, col: 0, table: 0 })
            }
          }}
        />
      )}
      {/* Table Component */}
      {!isMobile && (
        <TableGrid
          headers={tableGridHeadersEff}
          rows={tableGridRowsEff}
          headerGroups={tableGridHeaderGroupsEff}
          loading={loadingEff}
          tabIndex={isAnyCellFocused ? -1 : 0}
          totalRowCount={tableAriaRowCount}
          firstDataRowIndex={tableFirstDataRowIndex}
          tableId={tableId}
          ariaLabel={tableAriaLabelEff}
          onFocus={() => {
            if (!focusedCell) {
              setFocusedCell({ row: 0, col: 0 })
            }
          }}
          styleOverrides={tableStyleOverrides}
        />
      )}
      {/* Pagination Comp */}
      {(loading ||
        rows.length > ROWS_PER_PAGE_OPTIONS[0].value ||
        (!!paginationProps?.totalCount &&
          paginationProps.totalCount > ROWS_PER_PAGE_OPTIONS[0].value)) && (
        <TablePagination
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          numberOfPages={numberOfPages}
          showingRowsMessage={showingRowsMessage}
          handlePageChange={handlePageChange}
          handleRowsPerPageChange={handleRowsPerPageChange}
          loading={loadingEff}
          controlledTableId={tableId}
        />
      )}
      <AriaLiveRegion id={paginationAriaId} />
      <AriaLiveRegion id={sortAriaId} />
    </div>
  )
}
